import {
  AfterContentInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ContentChildren,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectorRef,
} from "@angular/core";
import { coerceBooleanProperty, BooleanInput } from "@vattenfall/util";
import { Subscription } from "rxjs";

@Component({
  selector: "ui-tabs-label",
  templateUrl: "./tab-child.component.html",
})
export class TabLabelComponent {
  @ViewChild(TemplateRef, { static: true }) templateRef!: TemplateRef<unknown>;
}

@Component({
  selector: "ui-tabs-panel",
  templateUrl: "./tab-child.component.html",
})
export class TabPanelComponent {
  @ViewChild(TemplateRef, { static: true }) templateRef!: TemplateRef<unknown>;
}

@Directive({
  selector: "ui-tabs-header",
})
export class HeaderDirective {
  constructor(elementRef: ElementRef<HTMLElement>) {
    elementRef.nativeElement.style.display = "block";
  }
}

@Directive({
  selector: "ui-tabs-footer",
})
export class FooterDirective {
  constructor(elementRef: ElementRef<HTMLElement>) {
    elementRef.nativeElement.style.display = "block";
  }
}

@Directive({
  selector: "ui-tabs-tab",
})
export class TabDirective {
  /**
   * Tab anchor name. This is used for connecting a tab label (anchor point) to a tab panel (page fragment). Tabs can be displayed by specifying the name as a page fragment identifier, e.g. `example.com#tab1`.
   */
  @Input() name?: string;
  /**
   * Whether this tab should automatically get focus when the page loads. The first tab will be focused by default. If the href location contains a fragment that match a tab name, then that tab will be focused regardless if `autofocus` has been declared or not.
   */
  @Input()
  get autofocus(): boolean {
    return this._autofocus;
  }
  set autofocus(autofocus: BooleanInput) {
    this._autofocus = coerceBooleanProperty(autofocus);
  }
  _autofocus = false;

  @ContentChild(TabLabelComponent)
  public readonly label!: TabLabelComponent;

  @ContentChild(TabPanelComponent)
  public readonly panel!: TabPanelComponent;

  /**
   * Whether this tab is the active tab or not.
   */
  public active = false;
}

/**
 * Vattenfall UI `ui-tabs` organize content into separate views where only one view can be visible at a time.
 *
 * Each tab must have an `name`. This is used for connecting a tab label (anchor point) to a tab panel (page fragment). Tabs can be displayed by specifying the name as a page fragment identifier, e.g. `example.com#tab1`.
 *
 * `<ui-tabs>` are accessible and can be navigated using keypress events alone. Right and left arrow buttons switches the active tab, while the down arrow button sets the focus on the tab panel. The tab button will tab out of the tab labels to the n tab index according to [accessability guidelines for tabbed interfaces](https://inclusive-components.design/tabbed-interfaces).
 */
@Component({
  selector: "ui-tabs",
  templateUrl: "./tabs.component.html",
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.Default,
})
export class TabsComponent implements AfterContentInit, OnDestroy {
  /**
   * Sets the active tab based on its name
   */
  @Input()
  get active() {
    return this._active;
  }
  set active(value: string) {
    if (value) {
      const newLabel = this.elementRef.nativeElement.querySelector<HTMLAnchorElement>(`[data-id="${value}"]`);
      const oldLabel = this.elementRef.nativeElement.querySelector<HTMLAnchorElement>("[aria-selected]");
      if (newLabel && oldLabel && !newLabel.isSameNode(oldLabel)) {
        this.switchTab(oldLabel, newLabel, false);
      }
    }
    this._active = value;
  }
  private _active = "";
  @Output() readonly activeChange = new EventEmitter<string>();

  @ContentChildren(TabDirective, { descendants: true })
  public readonly tabs = new QueryList<TabDirective>();

  private get labels() {
    return this.elementRef.nativeElement.querySelectorAll<HTMLAnchorElement>("[role='tab']");
  }
  private get panels() {
    return this.elementRef.nativeElement.querySelectorAll<HTMLAnchorElement>("[role='tabpanel']");
  }

  private tabsSubscription = Subscription.EMPTY;

  constructor(private elementRef: ElementRef<HTMLElement>, private changeDetectorRef: ChangeDetectorRef) {}

  ngAfterContentInit() {
    this.tabsSubscription = this.tabs.changes.subscribe(this.setActiveTab);
    this.setActiveTab(this.tabs);
  }

  ngOnDestroy() {
    this.tabsSubscription.unsubscribe();
  }

  /**
   * Handle clicking of labels for mouse users
   */
  onClick(event: MouseEvent) {
    // prevent the click to redirect the page to root page with hashed parameter.
    event.preventDefault();
    event.stopPropagation();
    const newLabel = event.currentTarget as HTMLAnchorElement;
    const oldLabel = this.elementRef.nativeElement.querySelector<HTMLAnchorElement>("[aria-selected]");
    if (oldLabel && !newLabel.isSameNode(oldLabel)) {
      this.switchTab(oldLabel, newLabel);
    }
  }

  /**
   * Handle keydown events for keyboard users
   */
  onKeydown(event: KeyboardEvent) {
    // Make sure that tabbing works
    if (event.key === "Tab") return;
    // prevent the click to redirect the page to root page with hashed parameter.
    event.preventDefault();
    event.stopPropagation();
    const oldLabel = event.currentTarget as HTMLAnchorElement;
    // Get the index of the current tab in the tabs node list
    const oldLabelIndex = Array.from(this.labels).findIndex((label) => label.isSameNode(oldLabel));
    // Work out which key the user is pressing
    switch (event.key) {
      // If the down key is pressed, move focus to the open panel
      case "ArrowDown": {
        this.panels[oldLabelIndex].focus();
        break;
      }
      // If the left or right key is pressed, switch to the previous tab
      case "ArrowLeft": {
        // Get the index of the new label, will be last in order if the old label is the first in order
        const newLabelIndex = oldLabelIndex === 0 ? this.labels.length - 1 : oldLabelIndex - 1;
        this.switchTab(oldLabel, this.labels.item(newLabelIndex));
        break;
      }
      // If the right or right key is pressed, switch to the next tab
      case "ArrowRight": {
        // Get the index of the new label, will be first in order if the old label is the last in order
        const newLabelIndex = oldLabelIndex === this.labels.length - 1 ? 0 : oldLabelIndex + 1;
        this.switchTab(oldLabel, this.labels.item(newLabelIndex));
        break;
      }
    }
  }

  private setActiveTab = (tabs: QueryList<TabDirective>) => {
    // Set the active tab in the prioritized order:
    // 1) The tab that match the name of the active property,
    // 2) The tab that has autofocus declared,
    // 3) The tab that match the href location hash (if any),
    tabs.forEach((tab) => {
      const hasActive = this.active !== "";
      const hasAutofocus = tabs.some((tabDirective) => tabDirective.autofocus as boolean);
      const isActive = tab.name === this.active;
      const isAutofocus = !hasActive && (tab.autofocus as boolean);
      const isHash = !hasActive && !hasAutofocus && window.location.hash.endsWith(`${tab.name}`);
      tab.active = isAutofocus || isActive || isHash;
    });
    // 4) The first tab in order of tabs
    if (tabs.length && !tabs.some((tabDirective) => tabDirective.active === false)) tabs.first.active = true;
  };

  private switchTab(oldLabel: HTMLAnchorElement, newLabel: HTMLAnchorElement, emitEvent = true) {
    // Get the indices of the new and old tabs to find the correct
    // tab panels to show and hide
    const newLabelIndex = Array.from(this.labels).findIndex((label) => label.isSameNode(newLabel));
    this.panels[newLabelIndex].hidden = false;
    const oldLabelIndex = Array.from(this.labels).findIndex((label) => label.isSameNode(oldLabel));
    this.panels[oldLabelIndex].hidden = true;
    // Set page fragment identifier
    history.replaceState(
      null,
      "",
      document.location.pathname + document.location.search + "#" + newLabel.getAttribute("data-id") ?? ""
    );
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this._active = newLabel.getAttribute("data-id")!;
    // Sets active property inside tabs
    this.tabs.forEach((tabDirective) => {
      tabDirective.active = tabDirective.name === this.active;
    });
    // Dispatch new active value
    if (emitEvent && newLabel.getAttribute("data-id")) this.activeChange.next(this.active);
    newLabel.focus();
    this.changeDetectorRef.detectChanges();
  }
}

@Directive({
  selector: "[role='tab']",
})
export class HTMLAnchorElementDirective implements OnChanges {
  @Input() name!: string;
  @Input() active!: boolean;
  constructor(private elementRef: ElementRef<HTMLAnchorElement>) {}
  ngOnChanges() {
    this.elementRef.nativeElement.setAttribute("data-id", `${this.name}`);
    this.elementRef.nativeElement.setAttribute("role", "tab");
    this.elementRef.nativeElement.setAttribute("href", `#${this.name}`);
    this.elementRef.nativeElement.setAttribute("id", `label-${this.name}`);
    if (this.active) {
      this.elementRef.nativeElement.setAttribute("tabindex", "0");
      this.elementRef.nativeElement.setAttribute("aria-selected", "");
      this.elementRef.nativeElement.classList.add("ds-font-bold", "!ds-border-primary-blue", "focus:ds-bg-background-blue");
    } else {
      this.elementRef.nativeElement.setAttribute("tabindex", "-1");
      this.elementRef.nativeElement.removeAttribute("aria-selected");
      this.elementRef.nativeElement.classList.remove("ds-font-bold", "!ds-border-primary-blue", "focus:ds-bg-background-blue");
    }
  }
}

@Directive({
  selector: "[role='tabpanel']",
})
export class HTMLSectionElementDirective implements OnChanges {
  @Input() name!: string;
  @Input() active!: boolean;
  constructor(private elementRef: ElementRef<HTMLAnchorElement>) {}
  ngOnChanges() {
    this.elementRef.nativeElement.hidden = !this.active;
    this.elementRef.nativeElement.setAttribute("id", `${this.name}`);
    this.elementRef.nativeElement.setAttribute("tabindex", "-1");
    this.elementRef.nativeElement.setAttribute("aria-labelledby", `${this.name}`);
  }
}
